import React, { useCallback, useEffect, useState } from "react";
import PageHeader from "@/components/PageHeader";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { PhoneCall, PlayCircle, CheckCircle2, XCircle, Ticket, Users, Timer, Monitor, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

const STATUT_STYLES = {
  en_attente: "bg-blue-100 text-blue-700 border-blue-200",
  appele: "bg-amber-100 text-amber-800 border-amber-200",
  en_service: "bg-violet-100 text-violet-700 border-violet-200",
  servi: "bg-emerald-100 text-emerald-700 border-emerald-200",
  absent: "bg-red-100 text-red-700 border-red-200",
};

const STATUT_LABELS = {
  en_attente: "En attente",
  appele: "Appelé",
  en_service: "En service",
  servi: "Servi",
  absent: "Absent",
};

function fmtDuree(sec) {
  if (sec == null) return "—";
  const m = Math.floor(sec / 60);
  const s = sec % 60;
  return m > 0 ? `${m}m ${s.toString().padStart(2, "0")}s` : `${s}s`;
}

function StatCard({ icon: Icon, label, value, tone = "blue", testid }) {
  const map = {
    blue: "bg-blue-100 text-blue-800",
    orange: "bg-orange-100 text-orange-700",
    violet: "bg-violet-100 text-violet-700",
    emerald: "bg-emerald-100 text-emerald-700",
    red: "bg-red-100 text-red-700",
  };
  return (
    <Card className="border border-slate-200 bg-white" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-center justify-between">
          <div>
            <div className="text-[10px] font-semibold uppercase tracking-[0.22em] text-slate-500">{label}</div>
            <div className="mt-1.5 font-display text-2xl font-bold text-slate-900">{value}</div>
          </div>
          <div className={`w-9 h-9 rounded-lg grid place-items-center ${map[tone]}`}>
            <Icon className="w-4 h-4" strokeWidth={1.75} />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

export default function FileAttente() {
  const { user } = useAuth();
  const isWriter = user?.role !== "auditeur";
  const [rows, setRows] = useState([]);
  const [stats, setStats] = useState(null);
  const [filterStatut, setFilterStatut] = useState("all");
  const [filterService, setFilterService] = useState("all");
  const [editing, setEditing] = useState(null);
  const [editForm, setEditForm] = useState({ service: "", motif: "", nom_client: "", telephone: "", guichet: "", statut: "" });
  const [saving, setSaving] = useState(false);

  const services = React.useMemo(() => {
    const set = new Set(rows.map((r) => r.service).filter(Boolean));
    return Array.from(set);
  }, [rows]);

  const load = useCallback(async () => {
    const params = {};
    if (filterStatut !== "all") params.statut = filterStatut;
    if (filterService !== "all") params.service = filterService;
    try {
      const [ticketsRes, statsRes] = await Promise.all([
        api.get("/tickets", { params }),
        api.get("/tickets/stats"),
      ]);
      setRows(ticketsRes.data);
      setStats(statsRes.data);
    } catch (e) {
      console.error(e);
    }
  }, [filterStatut, filterService]);

  useEffect(() => {
    load();
    const i = setInterval(load, 5000);
    return () => clearInterval(i);
  }, [load]);

  const callNext = async () => {
    try {
      const { data } = await api.post("/tickets/next");
      toast.success(`Ticket ${data.numero} appelé`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Aucun ticket");
    }
  };

  const action = async (id, path, label) => {
    try {
      await api.post(`/tickets/${id}/${path}`);
      toast.success(label);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  const startEdit = (t) => {
    setEditing(t);
    setEditForm({
      service: t.service || "",
      motif: t.motif || "",
      nom_client: t.nom_client || "",
      telephone: t.telephone || "",
      guichet: t.guichet || "",
      statut: t.statut || "en_attente",
    });
  };

  const submitEdit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await api.patch(`/tickets/${editing.id}`, editForm);
      toast.success("Ticket mis à jour");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    } finally {
      setSaving(false);
    }
  };

  const removeTicket = async (id) => {
    if (!window.confirm("Supprimer ce ticket ?")) return;
    try {
      await api.delete(`/tickets/${id}`);
      toast.success("Ticket supprimé");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur");
    }
  };

  return (
    <div>
      <PageHeader
        eyebrow="Module"
        title="File d'attente"
        description="Gestion des tickets d'accueil, appels au guichet et suivi temps réel."
        testid="file-attente-header"
        actions={
          <div className="flex items-center gap-2">
            <a href="/ecran" target="_blank" rel="noreferrer">
              <Button variant="outline" className="rounded-full" data-testid="link-display">
                <Monitor className="w-4 h-4 mr-2" /> Écran d&apos;affichage
              </Button>
            </a>
            <a href="/borne" target="_blank" rel="noreferrer">
              <Button variant="outline" className="rounded-full" data-testid="link-borne">
                <Ticket className="w-4 h-4 mr-2" /> Borne
              </Button>
            </a>
            {isWriter && (
              <Button onClick={callNext} className="bg-blue-900 hover:bg-blue-800 rounded-full" data-testid="call-next-btn">
                <PhoneCall className="w-4 h-4 mr-2" /> Appeler suivant
              </Button>
            )}
          </div>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-6">
        <StatCard icon={Users} label="Total du jour" value={stats?.total ?? "—"} tone="blue" testid="stat-total" />
        <StatCard icon={Ticket} label="En attente" value={stats?.en_attente ?? "—"} tone="orange" testid="stat-attente" />
        <StatCard icon={PlayCircle} label="En service" value={stats?.en_service ?? "—"} tone="violet" testid="stat-service" />
        <StatCard icon={CheckCircle2} label="Servis" value={stats?.servis ?? "—"} tone="emerald" testid="stat-servis" />
        <StatCard icon={Timer} label="Attente moyenne" value={fmtDuree(stats?.temps_attente_moyen_sec)} tone="blue" testid="stat-attente-avg" />
      </div>

      <Card className="border border-slate-200 bg-white">
        <CardContent className="p-4">
          <div className="flex flex-col md:flex-row gap-2 md:items-center md:justify-between mb-4">
            <div className="text-sm text-slate-500">
              Journée <span className="font-mono">{stats?.day || ""}</span> — auto-refresh 5s
            </div>
            <div className="flex gap-2">
              <Select value={filterStatut} onValueChange={setFilterStatut}>
                <SelectTrigger className="w-44" data-testid="ticket-statut-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous statuts</SelectItem>
                  {Object.keys(STATUT_LABELS).map((s) => <SelectItem key={s} value={s}>{STATUT_LABELS[s]}</SelectItem>)}
                </SelectContent>
              </Select>
              <Select value={filterService} onValueChange={setFilterService}>
                <SelectTrigger className="w-44" data-testid="ticket-service-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous services</SelectItem>
                  {services.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-24">Numéro</TableHead>
                  <TableHead>Service</TableHead>
                  <TableHead>Client</TableHead>
                  <TableHead>Statut</TableHead>
                  <TableHead>Guichet</TableHead>
                  <TableHead>Créé</TableHead>
                  <TableHead>Appelé</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {rows.length === 0 ? (
                  <TableRow><TableCell colSpan={8} className="text-center py-10 text-slate-400"><Ticket className="w-8 h-8 mx-auto mb-2 opacity-40" /> Aucun ticket</TableCell></TableRow>
                ) : rows.map((r) => (
                  <TableRow key={r.id} data-testid={`ticket-row-${r.numero}`}>
                    <TableCell><span className="font-display font-bold text-lg text-slate-900">{r.numero}</span></TableCell>
                    <TableCell className="text-slate-700 text-sm">{r.service}</TableCell>
                    <TableCell className="text-slate-700 text-sm">{r.nom_client || <span className="text-slate-400">Anonyme</span>}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={`font-medium ${STATUT_STYLES[r.statut] || ""}`}>{STATUT_LABELS[r.statut] || r.statut}</Badge>
                    </TableCell>
                    <TableCell className="text-slate-600 text-sm">{r.guichet || "—"}</TableCell>
                    <TableCell className="text-xs text-slate-500 font-mono">{new Date(r.created_at).toLocaleTimeString("fr-FR")}</TableCell>
                    <TableCell className="text-xs text-slate-500 font-mono">{r.called_at ? new Date(r.called_at).toLocaleTimeString("fr-FR") : "—"}</TableCell>
                    <TableCell className="text-right">
                      {isWriter && (
                        <div className="inline-flex gap-1">
                          {r.statut === "en_attente" && (
                            <Button variant="ghost" size="sm" onClick={() => action(r.id, "call", "Ticket appelé")} data-testid={`ticket-call-${r.numero}`}>
                              <PhoneCall className="w-4 h-4 text-blue-700" />
                            </Button>
                          )}
                          {r.statut === "appele" && (
                            <Button variant="ghost" size="sm" onClick={() => action(r.id, "serve", "Prise en charge")} data-testid={`ticket-serve-${r.numero}`}>
                              <PlayCircle className="w-4 h-4 text-violet-600" />
                            </Button>
                          )}
                          {(r.statut === "appele" || r.statut === "en_service") && (
                            <Button variant="ghost" size="sm" onClick={() => action(r.id, "complete", "Ticket servi")} data-testid={`ticket-complete-${r.numero}`}>
                              <CheckCircle2 className="w-4 h-4 text-emerald-600" />
                            </Button>
                          )}
                          {(r.statut === "appele" || r.statut === "en_attente") && (
                            <Button variant="ghost" size="sm" onClick={() => action(r.id, "absent", "Marqué absent")} data-testid={`ticket-absent-${r.numero}`}>
                              <XCircle className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                          <Button variant="ghost" size="sm" onClick={() => startEdit(r)} data-testid={`ticket-edit-${r.numero}`}>
                            <Pencil className="w-4 h-4 text-slate-500" />
                          </Button>
                          {(user?.role === "admin" || user?.role === "directeur") && (
                            <Button variant="ghost" size="sm" onClick={() => removeTicket(r.id)} data-testid={`ticket-delete-${r.numero}`}>
                              <Trash2 className="w-4 h-4 text-red-500" />
                            </Button>
                          )}
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      <Dialog open={!!editing} onOpenChange={(v) => !v && setEditing(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="font-display">Modifier le ticket {editing?.numero}</DialogTitle>
            <DialogDescription>Modifier les informations du ticket.</DialogDescription>
          </DialogHeader>
          <form onSubmit={submitEdit} className="space-y-3">
            <div className="space-y-1.5"><Label>Service</Label><Input value={editForm.service} onChange={(e) => setEditForm({ ...editForm, service: e.target.value })} data-testid="ticket-edit-service" /></div>
            <div className="space-y-1.5"><Label>Motif</Label><Input value={editForm.motif} onChange={(e) => setEditForm({ ...editForm, motif: e.target.value })} data-testid="ticket-edit-motif" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Nom client</Label><Input value={editForm.nom_client} onChange={(e) => setEditForm({ ...editForm, nom_client: e.target.value })} data-testid="ticket-edit-nom" /></div>
              <div className="space-y-1.5"><Label>Téléphone</Label><Input value={editForm.telephone} onChange={(e) => setEditForm({ ...editForm, telephone: e.target.value })} data-testid="ticket-edit-tel" /></div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Guichet</Label><Input value={editForm.guichet} onChange={(e) => setEditForm({ ...editForm, guichet: e.target.value })} data-testid="ticket-edit-guichet" /></div>
              <div className="space-y-1.5">
                <Label>Statut</Label>
                <Select value={editForm.statut} onValueChange={(v) => setEditForm({ ...editForm, statut: v })}>
                  <SelectTrigger data-testid="ticket-edit-statut"><SelectValue /></SelectTrigger>
                  <SelectContent>{Object.keys(STATUT_LABELS).map((s) => <SelectItem key={s} value={s}>{STATUT_LABELS[s]}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setEditing(null)}>Annuler</Button>
              <Button type="submit" disabled={saving} data-testid="ticket-edit-submit">Enregistrer</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
